// Site24x7 RUM Vue Plugin

import {
  initSite24x7Rum,
  setRumUserId,
  addRumBreadcrumb,
  isRumAvailable,
  trackPageNavigation,
  trackUserClick,
  trackFormInteraction,
  trackUserSession,
  trackPageExit,
  trackCustomEvent,
  initializeUserTracking,
  getEnvironmentInfo,
  getRumConfig,
} from '../services/site24x7Rum'

export default {
  install(Vue, options = {}) {
    // Initialize Site24x7 RUM
    const result = initSite24x7Rum(options.appKey, options.config)

    if (result.success) {
      // Initialize comprehensive user tracking
      initializeUserTracking()
    } else {
      console.warn('Site24x7 RUM Plugin: Failed to initialize -', result.error)
      return
    }

    // Add RUM instance to Vue prototype for global access
    Vue.prototype.$rum = {
      /**
       * Set user ID for RUM tracking with enhanced user information
       * @param {string|object} userInfo - User ID string or user object with detailed info
       * @param {string} userInfo.id - User ID
       * @param {string} userInfo.name - Full name
       * @param {string} userInfo.firstName - First name
       * @param {string} userInfo.lastName - Last name
       * @param {string} userInfo.email - Email address
       * @param {string} userInfo.userId - Alternative user ID field
       */
      setUserId(userInfo) {
        return setRumUserId(userInfo)
      },

      /**
       * Add breadcrumb for debugging
       * @param {string} message - Breadcrumb message
       * @param {string} category - Category (optional)
       */
      addBreadcrumb(message, category = 'vue') {
        return addRumBreadcrumb(message, category)
      },

      /**
       * Track page navigation
       * @param {string} fromRoute - Previous route path
       * @param {string} toRoute - New route path
       * @param {object} routeParams - Route parameters
       */
      trackPageNavigation(fromRoute, toRoute, routeParams = {}) {
        return trackPageNavigation(fromRoute, toRoute, routeParams)
      },

      /**
       * Track user clicks and interactions
       * @param {string} elementType - Type of element clicked
       * @param {string} elementId - Element ID or identifier
       * @param {string} elementText - Element text content
       * @param {object} metadata - Additional metadata
       */
      trackUserClick(elementType, elementId, elementText, metadata = {}) {
        return trackUserClick(elementType, elementId, elementText, metadata)
      },

      /**
       * Track form interactions
       * @param {string} formId - Form identifier
       * @param {string} action - Form action
       * @param {object} formData - Form data (sanitized)
       */
      trackFormInteraction(formId, action, formData = {}) {
        return trackFormInteraction(formId, action, formData)
      },

      /**
       * Track user session data
       * @param {object} sessionData - Session information
       */
      trackUserSession(sessionData = {}) {
        return trackUserSession(sessionData)
      },

      /**
       * Track page exit events
       * @param {string} exitReason - Reason for leaving
       * @param {object} pageMetrics - Page performance metrics
       */
      trackPageExit(exitReason, pageMetrics = {}) {
        return trackPageExit(exitReason, pageMetrics)
      },

      /**
       * Track custom events
       * @param {string} eventName - Custom event name
       * @param {object} eventData - Event data
       * @param {string} category - Event category
       */
      trackCustomEvent(eventName, eventData = {}, category = 'custom') {
        return trackCustomEvent(eventName, eventData, category)
      },

      /**
       * Check if RUM is available
       */
      isAvailable() {
        return isRumAvailable()
      },

      /**
       * Get environment information
       */
      getEnvironmentInfo() {
        return getEnvironmentInfo()
      },

      /**
       * Get RUM configuration
       */
      getConfig() {
        return getRumConfig()
      },
    }

    // Add global mixin for automatic page tracking
    Vue.mixin({
      beforeRouteEnter(to, from, next) {
        next((vm) => {
          // Track page navigation
          if (vm.$rum.isAvailable()) {
            vm.$rum.trackPageNavigation(from ? from.path : 'direct', to.path, {
              routeName: to.name,
              routeParams: to.params,
              routeQuery: to.query,
            })
          }
        })
      },

      beforeRouteUpdate(to, from, next) {
        // Track route updates
        if (this.$rum.isAvailable()) {
          this.$rum.trackPageNavigation(from.path, to.path, {
            routeName: to.name,
            routeParams: to.params,
            routeQuery: to.query,
            updateType: 'route_update',
          })
        }
        next()
      },

      beforeRouteLeave(to, from, next) {
        // Track page exit when leaving route
        if (this.$rum.isAvailable()) {
          this.$rum.trackPageExit('route_navigation', {
            fromRoute: from.path,
            toRoute: to.path,
          })
        }
        next()
      },
    })

    // Add global error handler integration
    const originalErrorHandler = Vue.config.errorHandler
    Vue.config.errorHandler = function (err, vm, info) {
      // Track error as custom event
      if (vm && vm.$rum && vm.$rum.isAvailable()) {
        vm.$rum.trackCustomEvent(
          'vue_error',
          {
            error: err.message,
            stack: err.stack,
            info: info,
            component: vm.$options.name || 'Unknown',
          },
          'error',
        )
      }

      // Call original error handler if it exists
      if (originalErrorHandler) {
        originalErrorHandler.call(this, err, vm, info)
      }
    }

    // Add Vue directive for tracking clicks
    Vue.directive('track-click', {
      bind(el, binding, vnode) {
        el.addEventListener('click', () => {
          if (vnode.context.$rum.isAvailable()) {
            const elementType = el.tagName.toLowerCase()
            const elementId = binding.value?.id || el.id || el.className || 'unknown'
            const elementText = binding.value?.text || el.textContent?.trim() || 'no text'
            const metadata = {
              directiveData: binding.value,
              component: vnode.context.$options.name || 'Unknown',
              ...binding.value?.metadata,
            }

            vnode.context.$rum.trackUserClick(elementType, elementId, elementText, metadata)
          }
        })
      },
    })

    // Add Vue directive for tracking form submissions
    Vue.directive('track-form', {
      bind(el, binding, vnode) {
        el.addEventListener('submit', () => {
          if (vnode.context.$rum.isAvailable()) {
            const formId = binding.value?.id || el.id || el.className || 'unknown'
            const formData = new FormData(el)
            const formDataObj = {}

            // Convert FormData to object (sanitized)
            for (let [key, value] of formData.entries()) {
              formDataObj[key] = value
            }

            vnode.context.$rum.trackFormInteraction(formId, 'submit', formDataObj)
          }
        })
      },
    })
  },
}
